import { RefObject, useEffect, useState } from "react"

interface UseOutOfViewOptions {
  threshold?: number
  rootMargin?: string
  once?: boolean
}

export function useOutOfView(ref: RefObject<HTMLElement | null>, options: UseOutOfViewOptions = {}): boolean {
  const { threshold = 0.1, rootMargin = "0px", once = false } = options

  const [isOutOfView, setIsOutOfView] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    // Fallback for iOS WebKit browsers that might have issues with IntersectionObserver
    const checkOutOfView = () => {
      if (!node) return false

      const rect = node.getBoundingClientRect()
      const windowHeight = window.innerHeight || document.documentElement.clientHeight
      const windowWidth = window.innerWidth || document.documentElement.clientWidth

      // Calculate visible area percentage
      const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0)
      const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0)
      const visibleArea = (visibleHeight * visibleWidth) / (rect.height * rect.width)

      // Element is out of view if visible area is less than threshold
      return visibleArea < threshold
    }

    // Primary method using IntersectionObserver
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // Invert the isIntersecting logic
          const isCurrentlyOutOfView = !entry.isIntersecting
          setIsOutOfView(isCurrentlyOutOfView)

          // Optional: Disconnect if we only want to track first out of view state
          if (once && isCurrentlyOutOfView) {
            observer.disconnect()
          }
        })
      },
      {
        threshold,
        rootMargin,
      }
    )

    // Add iOS-specific scroll and resize listeners for more reliable tracking
    const handleVisibilityCheck = () => {
      const outOfView = checkOutOfView()
      setIsOutOfView(outOfView)
    }

    // Observe the node

    observer.observe(node)

    // Add additional iOS-specific event listeners
    window.addEventListener("scroll", handleVisibilityCheck)
    window.addEventListener("resize", handleVisibilityCheck)

    // Initial check
    handleVisibilityCheck()

    // Cleanup
    return () => {
      observer.disconnect()
      window.removeEventListener("scroll", handleVisibilityCheck)
      window.removeEventListener("resize", handleVisibilityCheck)
    }
  }, [ref, threshold, rootMargin, once])

  return isOutOfView
}
